<template>
  <div class="statistics-page">
    <div class="page-header">
      <h2>Statistiques des ventes</h2>
    </div>

    <div class="filters-section">
      <div class="filters-grid">
        <div class="filter-group">
          <label>Client</label>
          <select v-model="filter.customer_reference" class="form-control">
            <option value="">Tous les clients</option>
            <option
              v-for="customer in customers"
              :key="customer.reference"
              :value="customer.reference"
            >
              {{ customer.fullName || customer.name }}
              {{ customer.society || "" }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label>Catégorie</label>
          <select v-model="filter.section_reference" class="form-control">
            <option value="">Toutes les catégories</option>
            <option
              v-for="section in sections"
              :key="section.reference"
              :value="section.reference"
            >
              {{ section.fullName || section.name }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label>Article</label>
          <select v-model="filter.item_reference" class="form-control">
            <option value="">Tous les articles</option>
            <option
              v-for="item in filteredItems"
              :key="item.reference"
              :value="item.reference"
            >
              {{ item.fullName || item.name }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label>Du <span class="required">*</span></label>
          <input v-model="filter.startDate" type="date" class="form-control" />
        </div>

        <div class="filter-group">
          <label>Au <span class="required">*</span></label>
          <input v-model="filter.endDate" type="date" class="form-control" />
        </div>
      </div>

      <div class="actions-buttons">
        <button @click="searchStatistics" class="btn btn-primary">
          <i class="bi bi-search"></i> Rechercher
        </button>
        <button @click="refreshStatistics" class="btn btn-secondary">
          <i class="bi bi-arrow-clockwise"></i> Actualiser
        </button>
        <button @click="printStatistics" class="btn btn-outline-primary">
          <i class="bi bi-printer"></i> Imprimer
        </button>
      </div>
    </div>

    <div class="statistics-table-container">
      <table class="statistics-table">
        <thead>
          <tr>
            <th>Nº</th>
            <th>Désignation</th>
            <th class="text-end">Quantité Achetée</th>
            <th class="text-end">Quantité Vendue</th>
            <th class="text-end">Stock Actuel</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="5" class="text-center">
              <div class="loading-spinner">Chargement...</div>
            </td>
          </tr>
          <tr v-else-if="statisticsReport.length === 0">
            <td colspan="5" class="text-center empty-state">
              <i class="bi bi-inbox"></i>
              <p>Aucune donnée disponible</p>
            </td>
          </tr>
          <tr v-else v-for="(item, index) in statisticsReport" :key="index">
            <td>{{ index + 1 }}</td>
            <td>{{ item.fullName || item.name || "N/A" }}</td>
            <td class="text-end text-success">
              +{{ formatNumber(item.purchasedQuantity || 0) }}
            </td>
            <td class="text-end text-danger">
              -{{ formatNumber(item.soldQuantity || 0) }}
            </td>
            <td class="text-end text-primary">
              {{ formatNumber(item.stockQuantity || 0) }}
            </td>
          </tr>
        </tbody>
        <tfoot v-if="statisticsReport.length > 0">
          <tr>
            <td colspan="2"><strong>Total</strong></td>
            <td class="text-end">
              <strong class="text-success">
                +{{ formatNumber(totalPurchased) }}
              </strong>
            </td>
            <td class="text-end">
              <strong class="text-danger">
                -{{ formatNumber(totalSold) }}
              </strong>
            </td>
            <td class="text-end">
              <strong class="text-primary">
                {{ formatNumber(totalStock) }}
              </strong>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "Statistics",
  data() {
    const today = new Date().toISOString().split("T")[0];
    const startOfYear = new Date(new Date().getFullYear(), 0, 1)
      .toISOString()
      .split("T")[0];

    return {
      loading: false,
      filter: {
        customer_reference: "",
        section_reference: "",
        item_reference: "",
        startDate: startOfYear,
        endDate: today,
      },
    };
  },
  computed: {
    ...mapGetters("customer", {
      customers: "getAll",
    }),
    ...mapGetters("category", {
      sections: "getAll",
    }),
    ...mapGetters("item", {
      items: "getAll",
    }),
    statisticsReport() {
      return this.$store.getters["sale/getStatisticsReport"] || [];
    },
    filteredItems() {
      if (!this.filter.section_reference) {
        return this.items || [];
      }
      return (
        (this.items || []).filter(
          (item) => item.section_reference === this.filter.section_reference
        ) || []
      );
    },
    totalPurchased() {
      return this.statisticsReport.reduce(
        (sum, item) => sum + parseFloat(item.purchasedQuantity || 0),
        0
      );
    },
    totalSold() {
      return this.statisticsReport.reduce(
        (sum, item) => sum + parseFloat(item.soldQuantity || 0),
        0
      );
    },
    totalStock() {
      return this.statisticsReport.reduce(
        (sum, item) => sum + parseFloat(item.stockQuantity || 0),
        0
      );
    },
  },
  async mounted() {
    await this.loadInitialData();
    await this.searchStatistics();
  },
  methods: {
    ...mapActions("customer", ["getAll"]),
    ...mapActions("category", ["getAll"]),
    ...mapActions("item", ["getAll"]),
    async loadInitialData() {
      try {
        await Promise.all([
          this.$store.dispatch("customer/getAll"),
          this.$store.dispatch("category/getAll"),
          this.$store.dispatch("item/getAll"),
        ]);
      } catch (error) {
        console.error("Error loading initial data:", error);
        this.$toast?.error?.("Erreur lors du chargement des données");
      }
    },
    async searchStatistics() {
      if (!this.filter.startDate || !this.filter.endDate) {
        this.$toast?.error?.("Veuillez sélectionner une période");
        return;
      }

      this.loading = true;
      try {
        await this.$store.dispatch("sale/fetchAllStatistics", this.filter);
      } catch (error) {
        console.error("Error fetching statistics:", error);
        this.$toast?.error?.("Erreur lors du chargement des statistiques");
      } finally {
        this.loading = false;
      }
    },
    async refreshStatistics() {
      const today = new Date().toISOString().split("T")[0];
      const startOfYear = new Date(new Date().getFullYear(), 0, 1)
        .toISOString()
        .split("T")[0];

      this.filter = {
        customer_reference: "",
        section_reference: "",
        item_reference: "",
        startDate: startOfYear,
        endDate: today,
      };
      await this.searchStatistics();
    },
    printStatistics() {
      window.print();
    },
    formatNumber(value) {
      const num = parseFloat(value || 0);
      return num.toLocaleString("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.statistics-page {
  padding: 2rem;
  background: #ffffff;
  min-height: calc(100vh - 60px);
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h2 {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.filters-section {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 16px;
  margin-bottom: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #4a5568;
}

.required {
  color: #ef4444;
}

.form-control {
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.actions-buttons {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.btn-secondary {
  background: #6c757d;
  color: #ffffff;
}

.btn-secondary:hover {
  background: #5a6268;
  transform: translateY(-2px);
}

.btn-outline-primary {
  background: #ffffff;
  color: #667eea;
  border: 2px solid #667eea;
}

.btn-outline-primary:hover {
  background: #667eea;
  color: #ffffff;
  transform: translateY(-2px);
}

.statistics-table-container {
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.statistics-table {
  width: 100%;
  border-collapse: collapse;
}

.statistics-table thead {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
}

.statistics-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.95rem;
}

.statistics-table tbody tr {
  border-bottom: 1px solid #e2e8f0;
  transition: background 0.2s;
}

.statistics-table tbody tr:hover {
  background: #f8f9fa;
}

.statistics-table td {
  padding: 1rem;
  font-size: 0.9rem;
  color: #4a5568;
}

.statistics-table tfoot {
  background: #f8f9fa;
  font-weight: 600;
}

.text-end {
  text-align: right;
}

.text-center {
  text-align: center;
}

.text-success {
  color: #10b981;
}

.text-danger {
  color: #ef4444;
}

.text-primary {
  color: #667eea;
}

.loading-spinner {
  padding: 2rem;
  color: #667eea;
}

.empty-state {
  padding: 3rem;
  color: #a0aec0;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  display: block;
}

@media print {
  .filters-section,
  .actions-buttons {
    display: none;
  }
}
</style>

